import { useEffect, useState } from 'react'
import { api } from '../api/client'

interface Stand {
  eingerichtet: boolean
  schluesselGueltig: boolean
  schluesselSeit: string | null
  letzteAnmeldung: string | null
}

function datum(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '–'
}

/**
 * Der Zugang des Assistenten: Schlüssel erzeugen und widerrufen.
 *
 * Der Schlüssel gehört in die Konfiguration des MCP-Servers auf dem Rechner, auf dem der
 * Assistent läuft. Er wird nur einmal angezeigt — gespeichert ist nur sein Abdruck.
 */
export function AssistentZugang() {
  const [stand, setStand] = useState<Stand | null>(null)
  const [schluessel, setSchluessel] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  async function laden() {
    try {
      setStand(await api.get<Stand>('/api/assistent/zugang'))
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Laden fehlgeschlagen')
    }
  }

  useEffect(() => {
    void laden()
  }, [])

  async function erzeugen() {
    if (
      stand?.schluesselGueltig &&
      !confirm('Einen neuen Schlüssel erzeugen? Der Assistent kommt erst wieder herein, wenn er den neuen hat.')
    ) {
      return
    }
    setFehler(null)
    try {
      const r = await api.post<{ schluessel: string }>('/api/assistent/zugang')
      setSchluessel(r.schluessel)
      await laden()
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Fehlgeschlagen')
    }
  }

  async function widerrufen() {
    if (!confirm('Den Schlüssel des Assistenten widerrufen? Er kann danach nichts mehr lesen oder ändern.')) {
      return
    }
    setFehler(null)
    try {
      await api.loeschen('/api/assistent/zugang')
      setSchluessel(null)
      await laden()
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Fehlgeschlagen')
    }
  }

  return (
    <div className="karte abschnitt">
      <h2>
        Assistent{' '}
        {stand && (
          <span className={`etikett ${stand.schluesselGueltig ? 'gruen' : 'grau'}`}>
            {stand.schluesselGueltig ? 'Zugang aktiv' : stand.eingerichtet ? 'widerrufen' : 'nicht eingerichtet'}
          </span>
        )}
      </h2>
      <p style={{ color: 'var(--grau-700)', fontSize: '0.9rem' }}>
        Der Assistent übernimmt Datenlieferungen und legt Ihnen unter <em>Klärungen</em> Berichte und
        Fragen vor. Er meldet sich mit einem eigenen Schlüssel an, nicht mit einem Passwort, und
        kann weder Zugänge verwalten noch seine eigenen Fragen beantworten.
      </p>
      {fehler && <div className="meldung fehler">{fehler}</div>}
      {stand?.eingerichtet && (
        <dl className="dienststand">
          <dt>Schlüssel seit</dt>
          <dd>{datum(stand.schluesselSeit)}</dd>
          <dt>Zuletzt angemeldet</dt>
          <dd>{datum(stand.letzteAnmeldung)}</dd>
        </dl>
      )}
      {schluessel && (
        <div className="meldung hinweis">
          <strong>Nur jetzt sichtbar.</strong> In der Konfiguration des MCP-Servers als
          Dienstschlüssel eintragen:
          <pre className="schluessel-anzeige">{schluessel}</pre>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="knopf-akzent" onClick={() => void erzeugen()}>
          {stand?.schluesselGueltig ? 'Neuen Schlüssel erzeugen' : 'Schlüssel erzeugen'}
        </button>
        {stand?.schluesselGueltig && (
          <button className="knopf-schlicht" onClick={() => void widerrufen()}>
            Widerrufen
          </button>
        )}
      </div>
    </div>
  )
}
